import {
  EmployeePaymentMethodType,
  OnboardingPeriodReportDto,
} from '@zorro/clients';
import { Button } from '@zorro/design-system/v2/Button';
import { Card } from '@zorro/design-system/v2/Card';
import { Counter } from '@zorro/design-system/v2/Counter';
import { Divider } from '@zorro/design-system/v2/Divider';
import { Drawer } from '@zorro/design-system/v2/Drawer';
import { Title } from '@zorro/design-system/v2/Title';
import { Group, Stack, Text } from '@zorro/web/design-system';
import { useEmployerFeatureFlag, useMonolithQuery } from '@zorro/web/ui-utils';
import { useState } from 'react';

import { CarrierLogoNext } from '../../../../../CarrierLogoNext';
import {
  CommunicationMessage,
  useEnrollmentCommunication,
} from '../../../../../EnrollmentCommunication';

const isEmployeeMode = false;

type EmployeePagePlansBenefitCardActionsRequiredContentProps = {
  onboardingPeriodReport: OnboardingPeriodReportDto;
};

const EmployeePagePlansBenefitCardActionsRequiredContent = ({
  onboardingPeriodReport,
}: EmployeePagePlansBenefitCardActionsRequiredContentProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  const majorMedicalBenefit = onboardingPeriodReport?.majorMedicalBenefit;

  const { communicationMessage } = useEnrollmentCommunication({
    onboardingPeriodId: onboardingPeriodReport.id,
    employerId: onboardingPeriodReport.employerId,
    employeeId: onboardingPeriodReport.employeeId,
    enrollmentStatus: onboardingPeriodReport?.enrollmentStatus,
    benefitId: majorMedicalBenefit?.id,
    effectiveFrom: onboardingPeriodReport?.effectiveFrom,
    planCarrierName: majorMedicalBenefit?.plan?.carrierName,
    planExternalId: majorMedicalBenefit?.plan?.externalID,
    isEmployeeMode,
  });

  if (
    !communicationMessage ||
    communicationMessage.flags.length === 0 ||
    !majorMedicalBenefit
  ) {
    return null;
  }

  const counter = (
    <Counter number={communicationMessage.flags.length} type="attention" />
  );
  const title =
    communicationMessage.flags.length > 1
      ? 'Actions required'
      : 'Action required';

  return (
    <>
      <Button
        onClick={() => setIsDrawerOpen(true)}
        leftSection={counter}
        variant="negative"
        mr="auto"
      >
        {title}
      </Button>
      <Drawer
        onClose={() => setIsDrawerOpen(false)}
        opened={isDrawerOpen}
        title={
          <Group gap="var(--space-8)" mb="var(--space-16)">
            {counter}
            <Title order={2}>{title} for the selected plan</Title>
          </Group>
        }
      >
        <Divider my="var(--space-16)" />

        <Group>
          <Card
            variant="white-shadow"
            pos="relative"
            mih="3.75rem"
            miw="7.5rem"
            bdrs="sm"
          >
            <CarrierLogoNext
              style={{ paddingBlock: '0.5rem', paddingInline: '15%' }}
              plan={{
                carrierName: majorMedicalBenefit.plan.carrierName,
              }}
              sizes="120px"
            />
          </Card>
          <Stack>
            <Text>{majorMedicalBenefit.plan.name}</Text>
          </Stack>
        </Group>
        <Divider my="var(--space-16)" />

        {communicationMessage && (
          <CommunicationMessage
            sectionTitle={communicationMessage.sectionTitle}
            flags={communicationMessage.flags}
            employeeId={onboardingPeriodReport.employeeId}
            employerId={onboardingPeriodReport.employerId}
            onboardingPeriodId={onboardingPeriodReport.id}
            isEmployeeMode={isEmployeeMode}
            shouldShowZorroPayButton={
              onboardingPeriodReport.paymentMethod !==
              EmployeePaymentMethodType.SELF_PAY
            }
            shouldShowPaymentMethodType={
              onboardingPeriodReport.paymentMethod ===
              EmployeePaymentMethodType.COMBINED
            }
          />
        )}
      </Drawer>
    </>
  );
};

type Props = {
  onboardingPeriodId: string;
  employerId: string;
};

export const EmployeePagePlansBenefitCardActionsRequired = ({
  onboardingPeriodId,
  employerId,
}: Props) => {
  const { isOn: isActionsRequiredEnabled } = useEmployerFeatureFlag(
    'with_employee_page_actions_required',
    employerId
  );

  const { data: onboardingPeriodReport, isLoading } = useMonolithQuery({
    method: 'onboardingPeriodReportsControllerGenerateOnboardingPeriodReport',
    params: isActionsRequiredEnabled && [onboardingPeriodId],
  });

  if (!onboardingPeriodReport || isLoading || !isActionsRequiredEnabled) {
    return null;
  }

  return (
    <EmployeePagePlansBenefitCardActionsRequiredContent
      onboardingPeriodReport={onboardingPeriodReport}
    />
  );
};
